import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Tag, Wand2, PowerOff, Power, Eye, ChevronUp, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const MATCH_LABELS = { contains: "contains", equals: "equals", starts_with: "starts with" };

export default function MoneyRules() {
  const [rules, setRules] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([api.get("/money/merchant-rules"), api.get("/money/categories")]);
    setRules(r.data); setCats(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const catBy = Object.fromEntries(cats.map((c) => [c.id, c]));

  const applyNow = async (id) => {
    const res = await api.post(`/money/merchant-rules/${id}/apply`);
    toast.success(`Re-tagged ${res.data.retroactively_applied} past transactions`);
    load();
  };
  const remove = async (id, pattern) => {
    if (!confirm(`Delete rule "${pattern}"?`)) return;
    await api.delete(`/money/merchant-rules/${id}`);
    toast.success("Rule deleted"); load();
  };
  const toggleActive = async (r) => {
    await api.patch(`/money/merchant-rules/${r.id}`, { active: !r.active });
    load();
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rules.length) return;
    const next = [...rules];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRules(next);   // optimistic
    await api.post("/money/merchant-rules/reorder", { ordered_ids: next.map((r) => r.id) });
  };

  if (loading) return <div className="text-sm text-surface-onVariant">Loading rules…</div>;

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-surface-on tracking-tight">Merchant rules</h3>
          <p className="text-xs text-surface-onVariant">Rename a payee once — auto-tag every past and future transaction</p>
        </div>
        <button
          data-testid="add-rule"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:brightness-110 active:scale-[0.98] transition-[transform,filter] duration-200 ease-md"
        >
          <Plus className="w-4 h-4" /> Add rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="rounded-hero bg-surface-container p-10 text-center">
          <Wand2 className="w-8 h-8 mx-auto text-surface-onVariant" />
          <p className="text-sm text-surface-onVariant mt-3">No rules yet. Create one to auto-categorise your transactions.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="rules-list">
          {rules.map((r, idx) => {
            const c = catBy[r.category_id];
            return (
              <li key={r.id} className="bg-surface-container rounded-2xl px-4 py-3 flex items-center gap-4">
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-0.5 rounded hover:bg-primary/8 disabled:opacity-30 transition-colors" data-testid={`rule-up-${idx}`}>
                    <ChevronUp className="w-4 h-4 text-surface-onVariant" />
                  </button>
                  <button onClick={() => move(idx, 1)} disabled={idx === rules.length - 1} className="p-0.5 rounded hover:bg-primary/8 disabled:opacity-30 transition-colors" data-testid={`rule-down-${idx}`}>
                    <ChevronDown className="w-4 h-4 text-surface-onVariant" />
                  </button>
                </div>
                <div className="w-6 text-center text-xs font-medium tabular-nums text-surface-onVariant">{idx + 1}</div>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: (c?.color || "#6750A4") + "22" }}>
                  <Tag className="w-4 h-4" style={{ color: c?.color || "#6750A4" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-surface-on truncate">
                    <span className="font-medium">Payee {MATCH_LABELS[r.match_type]}</span> "<span className="font-mono">{r.pattern}</span>" → <span className="font-medium">{c?.name || "?"}</span>
                  </div>
                  <div className="text-[10px] text-surface-onVariant tracking-widest uppercase mt-0.5">
                    {r.active ? "active" : "paused"} · {r.applied_count} retagged
                  </div>
                </div>
                <button onClick={() => applyNow(r.id)} className="p-2 rounded-full text-primary hover:bg-primary/8 transition-colors" title="Apply now" aria-label="Apply">
                  <Wand2 className="w-4 h-4" />
                </button>
                <button onClick={() => toggleActive(r)} className="p-2 rounded-full text-surface-onVariant hover:bg-primary/8 transition-colors" title={r.active ? "Pause" : "Activate"} aria-label="Toggle">
                  {r.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                </button>
                <button onClick={() => remove(r.id, r.pattern)} className="p-2 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger transition-colors" title="Delete" aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <RuleDialog open={open} onOpenChange={setOpen} cats={cats} onSaved={load} />
    </div>
  );
}

function RuleDialog({ open, onOpenChange, cats, onSaved }) {
  const [pattern, setPattern] = useState("");
  const [matchType, setMatchType] = useState("contains");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [retro, setRetro] = useState(true);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setPattern(""); setMatchType("contains"); setCaseSensitive(false); setCategoryId(""); setRetro(true); setPreview(null); }
  }, [open]);

  useEffect(() => {
    if (!open || !pattern.trim()) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/money/merchant-rules/preview", { params: { pattern, match_type: matchType, case_sensitive: caseSensitive } });
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [pattern, matchType, caseSensitive, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pattern.trim() || !categoryId) return;
    setSaving(true);
    try {
      const r = await api.post("/money/merchant-rules", { pattern: pattern.trim(), match_type: matchType, case_sensitive: caseSensitive, category_id: categoryId, apply_retroactive: retro });
      toast.success(retro ? `Rule saved · ${r.data.retroactively_applied} past retagged` : "Rule saved");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-surface rounded-hero border-none p-6" data-testid="rule-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">New merchant rule</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-surface-onVariant mt-1">Match by payee text — every current and future expense will land in the chosen category.</DialogDescription>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <div className="text-[10px] font-medium tracking-widest uppercase text-surface-onVariant mb-1">Payee match</div>
            <div className="flex gap-2 items-center">
              <Select value={matchType} onValueChange={setMatchType}>
                <SelectTrigger data-testid="rule-match-type" className="w-36 bg-surface-containerHigh border-b-2 border-outline focus:border-primary rounded-t-lg h-11 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl bg-surface">
                  <SelectItem value="contains">contains</SelectItem>
                  <SelectItem value="starts_with">starts with</SelectItem>
                  <SelectItem value="equals">equals</SelectItem>
                </SelectContent>
              </Select>
              <input data-testid="rule-pattern" value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder='e.g. "Woolworths"' className="flex-1 bg-transparent outline-none text-surface-on py-2 border-b-2 border-outline focus:border-primary" />
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs text-surface-onVariant">
              <input type="checkbox" checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} className="accent-primary" />
              Case-sensitive
            </label>
          </div>
          <div>
            <div className="text-[10px] font-medium tracking-widest uppercase text-surface-onVariant mb-1">Category</div>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger data-testid="rule-category" className="w-full bg-surface-containerHigh border-b-2 border-outline focus:border-primary rounded-t-lg h-11 text-sm"><SelectValue placeholder="Choose category" /></SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface">
                {cats.filter((c) => c.kind === "expense").map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {preview && preview.match_count > 0 && (
            <div className="bg-primary-container/40 rounded-2xl p-4 text-primary-onContainer text-sm" data-testid="rule-preview">
              <div className="flex items-center gap-2 font-medium"><Eye className="w-4 h-4" /> {preview.match_count} past transaction{preview.match_count !== 1 ? "s" : ""} match</div>
              <ul className="mt-2 space-y-0.5 text-xs">
                {preview.sample.slice(0, 3).map((s, i) => <li key={i} className="truncate">· {s.payee} <span className="text-primary/70">· {s.date}</span></li>)}
                {preview.match_count > 3 && <li className="text-xs opacity-70">+ {preview.match_count - 3} more</li>}
              </ul>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-surface-on">
            <input type="checkbox" checked={retro} onChange={(e) => setRetro(e.target.checked)} className="accent-primary" />
            Also re-tag matching past transactions
          </label>
          <button data-testid="rule-save" type="submit" disabled={saving || !pattern.trim() || !categoryId} className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
            {saving ? "Saving…" : "Save rule"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
